import React, { useEffect, useState } from "react";
import {
  deletePaper,
  getAllPapers,
  getUserActivities,
  getUserProfile,
} from "../api/Api";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import LanguageIcon from "@mui/icons-material/Language";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import CustomButton from "./CustomButton";
import DeleteModel from "./modals/DeleteModel";
import avatar from "../assets/u_avatar.png";
import CustomActivityCalender from "./CustomActivityCalender";
import CustomSkeleton from "./CustomSkeleton";
import styles from "./UserProfileComponent.module.css";
import ActivityLog from "./ActivityLog";
import PaperList from "./cards/PaperList";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "./ui/Pagination";
import { showToast } from "../utils/toastUtils";


function UserProfileComponent() {
  const [userData, setUserData] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteModelPaperId, setDeleteModelPaperId] = useState(null); // paper or initiative
  const [contributions, setContributions] = useState(null);
  const [open, setOpen] = useState(false);
  const openAlert = () => setOpen(true); // for open initiative delete model
  const closeAlert = () => setOpen(false); // for close initiative delete model
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const [totalItems, setTotalItems] = useState(0);

  const handleMenuItemClick = async (item, value) => {
    setDeleteModelPaperId(value);
    openAlert();
  };

  const handleDeletePaper = (paperId) => {
    deletePaper(paperId).then((response) => {
      if (response.status === 200) {
        fetchPapers(currentPage);
        showToast("Paper deleted successfully", "success");
      } else alert(response.message);
    });
    closeAlert();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleEditProfile = () => {
    handleMenuClose();
    navigate("/edit-profile");
  };

  useEffect(() => {
    setLoading(true);
    getUserProfile()
      .then((response) => {
        if (response.status === 200) {
          console.log("User Profile Data:", response.data);
          setUserData(response.data);
        } else {
          setError(response.message);
        }
      })
      .catch((error) => setError(error.message));
    getUserActivities()
      .then((response) => {
        if (response.status === 200) {
          setContributions(response.data);
        } else {
          setError(response.message);
        }
      })
      .catch((error) => setError(error.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPapers(currentPage);
  }, [currentPage]);

  const fetchPapers = (page) => {
    console.log("Fetching page:", page);
    setLoading(true);
    getAllPapers(page)
      .then((response) => {
        let papersArray = [];
        if (Array.isArray(response.data)) {
          papersArray = response.data;
        } else if (Array.isArray(response.data?.data)) {
          papersArray = response.data.data;
        }
        setPapers(papersArray);
        setTotalItems(
          response.pagination?.total_items ||
            response.data?.pagination?.total_items ||
            papersArray.length
        );
      })
      .catch((error) => setError(error.message))
      .finally(() => setLoading(false));
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fullName = `${userData?.firstname || ""} ${userData?.lastname || ""}`;
  console.log("----------------------------------", papers);
  if (error) return <div>Error: {error}</div>;

  return (
    <section className={`container ${styles.container} mt-5`}>
      {/* Profile details */}
      <div className="w-100 p-3 border-bottom border-color-secondary">
        <div className="d-flex align-items-start">
          {/* Profile Image */}
          <div className="d-block d-lg-flex">
            <div>
              <img
                src={avatar || "https://picsum.photos/id/64/170/170"}
                alt={`${userData?.username}'s profile`}
                className="rounded-circle"
                width="170"
                height="170"
              />
            </div>
            {/* User Information */}
            <div
              className={`mx-lg-4 mt-3 mt-lg-0 d-flex flex-column ${styles.userDetails}`}
            >
              <h3 className="mb-2 fw-bold text-capitalize">
                {loading ? <CustomSkeleton /> : fullName}
              </h3>
              {/* Job Title and Institute */}
              {loading ? (
                <CustomSkeleton />
              ) : (
                <div className="d-sm-inline-flex">
                  <p className="mb-2">
                    {userData?.jobTitle}
                    {userData?.jobTitle && userData?.institute?.name && " | "}
                    {userData?.institute?.name}
                  </p>
                </div>
              )}
              {/* Social Icons and Follower Info */}
              {loading ? (
                <CustomSkeleton />
              ) : (
                <div className="d-sm-inline-flex align-items-center mb-2 mb-sm-3">
                  <div
                    className={`d-flex gap-2 ${styles.socialIconContainer} mb-2 mb-sm-0`}
                  >
                    <GitHubIcon color="action" />
                    <LinkedInIcon color="action" />
                    <TwitterIcon color="action" />
                    <LanguageIcon color="action" />
                    <AlternateEmailIcon color="action" />
                  </div>
                  <p className="m-0">
                    <span className="mx-3 d-none d-sm-inline-flex">|</span>
                    <span className="text-color-tertiary me-2">
                      Followers:
                    </span>{" "}
                    78k <span className="mx-2">•</span>
                    <span className="text-color-tertiary mx-2">
                      Following:
                    </span>{" "}
                    54k
                  </p>
                </div>
              )}
              {/* Country and Last Seen Info */}
              <p className="text-color-tertiary mb-2 mb-sm-3 fs-6">
                {loading ? (
                  <CustomSkeleton className="w-50" />
                ) : (
                  userData?.country
                )}
              </p>
              {loading ? (
                <CustomSkeleton />
              ) : (
                <p className="text-color-tertiary fs-6">
                  Joined 2 months ago • Last seen yesterday
                </p>
              )}
            </div>
          </div>
          {/* More Options */}
          <div className="ms-auto ">
            <IconButton
              aria-label="more"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
            >
              ...
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleEditProfile}>Edit Profile</MenuItem>
            </Menu>
          </div>
        </div>
        <CustomButton
          className="my-3 px-5"
          variant="custom-primary-outline-button"
          rounded
        >
          Follow
        </CustomButton>
      </div>
      {/* Paper details */}
      {/* <div className="pt-5 pe-4 d-flex flex-row-reverse">
          <CustomButton
            className={"ms-auto py-2"}
            rounded={true}
            onClick={() => {}}
            pre={<AddRoundedIcon sx={{ width: 20 }} />}
          >
            NEW ARTICLE
          </CustomButton>
        </div> */}

      <CustomActivityCalender values={contributions} />

      {/* Paper List with Pagination */}
      <PaperList
        papers={papers}
        handleMenuItemClick={handleMenuItemClick}
        loading={loading}
      />

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        pageSize={pageSize}
      />

      <div style={{ paddingBottom: "1.5rem" }}>
        <ActivityLog title={"Activity Log"} type="user" />
      </div>
      <DeleteModel
        open={open}
        closeAlert={closeAlert}
        onClick={() => handleDeletePaper(deleteModelPaperId)}
        type="Paper"
      />
    </section>
  );
}

export default UserProfileComponent;
